// src/components/shops/postTemplates/QuietCard.tsx
//
// The everyday workhorse. Deliberately unmemorable — right for "we're shut
// Monday" — and the one template that never fights its own content. Every shop
// needs one that doesn't shout, which is why it leads the unlock order.

import { cachedUrl } from '../../../lib/storageUrl'
import SocialBrewBadge from '../SocialBrewBadge'
import { categoryLabel, type TemplateProps } from './types'

export default function QuietCard({
  title, body, photos, category, shopName, rating, ratingCount, scale = 1,
}: TemplateProps) {
  const px = (n: number) => `${n * scale}px`
  const label = categoryLabel(category)

  return (
    <div
      className="bg-cream-50 border border-cream-200 overflow-hidden"
      style={{ borderRadius: px(14), padding: px(14) }}
      data-shop={shopName}
    >
      {photos.length > 0 && (
        <div
          className="grid"
          style={{
            gap: px(4),
            marginBottom: px(12),
            gridTemplateColumns: photos.length === 1 ? '1fr' : '1fr 1fr',
          }}
        >
          {photos.map((url, i) => (
            <div
              key={i}
              className="overflow-hidden"
              style={{
                borderRadius: px(9),
                // The row-spanning first photo would be a very tall square at
                // full width, so it takes the same 16/9 a lone photo does.
                aspectRatio: photos.length === 1 || (photos.length === 3 && i === 0) ? '16 / 9' : '1 / 1',
                // With three photos the first spans the row, so the odd one out
                // reads as intentional rather than as a gap.
                gridColumn: photos.length === 3 && i === 0 ? 'span 2' : undefined,
              }}
            >
              <img src={cachedUrl(url)} alt="" loading="lazy" className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      )}

      <p className="text-coffee-800 font-semibold leading-snug" style={{ fontSize: px(15) }}>
        {title}
      </p>
      {body && (
        <p className="text-coffee-500 leading-relaxed" style={{ fontSize: px(13), marginTop: px(4) }}>
          {body}
        </p>
      )}
      {label && (
        <p
          className="text-caramel font-semibold uppercase"
          style={{ fontSize: px(10), letterSpacing: '0.1em', marginTop: px(10) }}
        >
          {label}
        </p>
      )}

      <div style={{ marginTop: px(12) }}>
        <SocialBrewBadge rating={rating} ratingCount={ratingCount} tone="light" scale={scale} />
      </div>
    </div>
  )
}
